import React from 'react';
import { motion } from '@/lib/motion-wrapper';
import { ChevronRight } from 'lucide-react';

// URLs for marketplace logos
const flipkartLogo = 'https://logos-world.net/wp-content/uploads/2020/11/Flipkart-Logo.png';
const amazonLogo = 'https://logos-world.net/wp-content/uploads/2020/04/Amazon-Logo-2000-present.jpg';
const meeshoLogo = 'https://img-cdn.thepublive.com/filters:format(webp)/tice-news-prod/media/post_attachments/6e77b763-742.jpg';
const jioMartLogo = 'https://seeklogo.com/images/J/jiomart-logo-DDF12BB25D-seeklogo.com.png';
const blinkitLogo = 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2f/Blinkit-yellow-app-icon.svg/1024px-Blinkit-yellow-app-icon.svg.png';

const MarketplaceSection: React.FC = () => {
  return (
    <section className="py-20 bg-white relative overflow-hidden">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 font-playfair text-brand-800">Shop Natural Puff On</h2>
          <div className="w-24 h-1 bg-gold-500 mx-auto mb-8"></div>
          <p className="text-xl text-brand-700 max-w-3xl mx-auto">
            Our premium makhana snacks are also available on your favorite online marketplaces.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -20, y: 0 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-cream-50 rounded-xl p-8 shadow-md hover:shadow-lg transition-transform duration-150"
          >
            <div className="flex justify-center mb-6">
              <img 
                src={flipkartLogo} 
                alt="Flipkart" 
                className="h-16 object-contain" 
              />
            </div>
            <p className="text-lg text-brand-700 mb-6">
              Shop our complete range of flavors with fast delivery options across India.
            </p>
            <a 
              href="https://www.flipkart.com/search?q=natural%20puff" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="inline-flex items-center text-brand-600 font-medium hover:text-brand-700 transition-colors"
            >
              Shop on Flipkart <ChevronRight className="h-4 w-4 ml-1" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20, y: 0 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-cream-50 rounded-xl p-8 shadow-md hover:shadow-lg transition-transform duration-150"
          >
            <div className="flex justify-center mb-6">
              <img 
                src={amazonLogo} 
                alt="Amazon" 
                className="h-16 object-contain" 
              />
            </div>
            <p className="text-lg text-brand-700 mb-6">
              Enjoy the convenience of Amazon Prime delivery with our premium makhana snacks.
            </p>
            <a 
              href="https://www.amazon.in/s?k=natural+puff+makhana" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="inline-flex items-center text-brand-600 font-medium hover:text-brand-700 transition-colors"
            >
              Shop on Amazon <ChevronRight className="h-4 w-4 ml-1" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20, y: 0 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-cream-50 rounded-xl p-8 shadow-md hover:shadow-lg transition-transform duration-150"
          >
            <div className="flex justify-center mb-6">
              <img 
                src={meeshoLogo} 
                alt="Meesho" 
                className="h-16 object-contain" 
              />
            </div>
            <p className="text-lg text-brand-700 mb-6">
              Discover our products at unbeatable prices with Meesho's best deals and offers.
            </p>
            <a 
              href="https://www.meesho.com/search?q=natural+puff" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="inline-flex items-center text-brand-600 font-medium hover:text-brand-700 transition-colors"
            >
              Shop on Meesho <ChevronRight className="h-4 w-4 ml-1" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20, y: 0 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-cream-50 rounded-xl p-8 shadow-md hover:shadow-lg transition-transform duration-150"
          >
            <div className="flex justify-center mb-6">
              <img 
                src={jioMartLogo} 
                alt="Jio Mart" 
                className="h-16 object-contain" 
              />
            </div>
            <p className="text-lg text-brand-700 mb-6">
              Get our healthy snacks delivered to your doorstep with JioMart's reliable service.
            </p>
            <a 
              href="https://www.jiomart.com/search?q=natural+puff" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="inline-flex items-center text-brand-600 font-medium hover:text-brand-700 transition-colors"
            >
              Shop on Jio Mart <ChevronRight className="h-4 w-4 ml-1" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20, y: 0 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="bg-cream-50 rounded-xl p-8 shadow-md hover:shadow-lg transition-transform duration-150"
          >
            <div className="flex justify-center mb-6">
              <img 
                src={blinkitLogo} 
                alt="Blinkit" 
                className="h-16 object-contain" 
              />
            </div>
            <p className="text-lg text-brand-700 mb-6">
              Get instant delivery of our fresh makhana snacks in minutes with Blinkit.
            </p>
            <a 
              href="https://blinkit.com/prn/x/prid/719009" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="inline-flex items-center text-brand-600 font-medium hover:text-brand-700 transition-colors"
            >
              Shop on Blinkit <ChevronRight className="h-4 w-4 ml-1" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default MarketplaceSection;